import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import ReadDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-devices-response';
import WanderingBoundary from 'features/wandering-boundary-settings/domain/models/wandering-boundary';
import WanderingBoundaryLocation from 'features/wandering-boundary-settings/domain/models/wandering-boundary-location';
import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import ReadDomipDevicesResponse from '../models/read-domip-devices-response';

const wanderingBoundaryBaseUrl = '/api/v1/WanderingBoundary';

enum WanderingBoundaryQueryPaths {
  ReadWanderingBoundaryLocations = 'ReadWanderingBoundaryLocations',
  ReadWanderingBoundaries = 'ReadWanderingBoundaries',
  CreateWanderingBoundary = 'CreateWanderingBoundary',
  UpdateWanderingBoundary = 'UpdateWanderingBoundary',
  RemoveWanderingBoundary = 'RemoveWanderingBoundary',
  ReadWanderingBoundaryPassages = 'ReadWanderingBoundaryPassages',
  CreateWanderingBoundaryPassage = 'CreateWanderingBoundaryPassage',
  UpdateWanderingBoundaryPassage = 'UpdateWanderingBoundaryPassage',
  RemoveWanderingBoundaryPassage = 'RemoveWanderingBoundaryPassage',
  ReadDevices = 'ReadDevices',
  ReadDomipDevices = 'ReadDomipDevices',
}

const readWanderingBoundaryLocations = async (axiosInstance: AxiosInstance, signal?: AbortSignal): Promise<WanderingBoundaryLocation[]> => {
  const response = await axiosInstance.get(`${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.ReadWanderingBoundaryLocations}`, {
    signal,
  });
  return response.data;
};

const readWanderingBoundaries = async (
  axiosInstance: AxiosInstance,
  locationId: string,
  signal?: AbortSignal,
): Promise<Array<WanderingBoundary>> => {
  const response = await axiosInstance.get(
    `${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.ReadWanderingBoundaries}/${locationId}`,
    { signal },
  );
  return response.data;
};

const createWanderingBoundary = async (axiosInstance: AxiosInstance, wanderingBoundary: WanderingBoundary): Promise<WanderingBoundary> => {
  const response = await axiosInstance.post(
    `${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.CreateWanderingBoundary}`,
    wanderingBoundary,
  );
  return response.data;
};

const updateWanderingBoundary = async (axiosInstance: AxiosInstance, wanderingBoundary: WanderingBoundary): Promise<WanderingBoundary> => {
  const response = await axiosInstance.put(
    `${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.UpdateWanderingBoundary}`,
    wanderingBoundary,
  );
  return response.data;
};

const removeWanderingBoundary = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.RemoveWanderingBoundary}/${id}`);
};

const readDevices = async (axiosInstance: AxiosInstance, locationId: string, signal?: AbortSignal): Promise<Array<ReadDevicesResponse>> => {
  const response = await axiosInstance.get(`${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.ReadDevices}/${locationId}`, {
    signal,
  });
  return response.data;
};

const readDomipDevices = async (
  axiosInstance: AxiosInstance,
  locationId: string,
  signal?: AbortSignal,
): Promise<Array<ReadDomipDevicesResponse>> => {
  const response = await axiosInstance.get(`${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.ReadDomipDevices}/${locationId}`, {
    signal,
  });
  return response.data;
};

const readWanderingBoundaryPassages = async (
  axiosInstance: AxiosInstance,
  locationId: string,
  signal?: AbortSignal,
): Promise<Array<WanderingBoundaryPassage>> => {
  const response = await axiosInstance.get(
    `${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.ReadWanderingBoundaryPassages}/${locationId}`,
    { signal },
  );
  return response.data;
};

const createWanderingBoundaryPassage = async (
  axiosInstance: AxiosInstance,
  wanderingBoundaryPassage: WanderingBoundaryPassage,
): Promise<WanderingBoundaryPassage> => {
  const response = await axiosInstance.post(
    `${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.CreateWanderingBoundaryPassage}`,
    wanderingBoundaryPassage,
  );
  return response.data;
};

const updateWanderingBoundaryPassage = async (
  axiosInstance: AxiosInstance,
  wanderingBoundaryPassage: WanderingBoundaryPassage,
): Promise<WanderingBoundaryPassage> => {
  const response = await axiosInstance.put(
    `${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.UpdateWanderingBoundaryPassage}`,
    wanderingBoundaryPassage,
  );
  return response.data;
};

const removeWanderingBoundaryPassage = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${wanderingBoundaryBaseUrl}/${WanderingBoundaryQueryPaths.RemoveWanderingBoundaryPassage}/${id}`);
};

export const useReadWanderingBoundaryLocations = (): UseQueryResult<WanderingBoundaryLocation[], unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaryLocations],
    queryFn: ({ signal }) => readWanderingBoundaryLocations(authenticatedInstance, signal),
  });
};

export const useLazyReadWanderingBoundaries = (locationId: string): UseQueryResult<Array<WanderingBoundary>, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaries, locationId],
    queryFn: ({ signal }) => readWanderingBoundaries(authenticatedInstance, locationId, signal),
    enabled: false,
  });
};

export const useCreateWanderingBoundary = (): UseMutationResult<WanderingBoundary, Error, WanderingBoundary, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [WanderingBoundaryQueryPaths.CreateWanderingBoundary],
    mutationFn: (wanderingBoundary: WanderingBoundary) => createWanderingBoundary(authenticatedInstance, wanderingBoundary),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaryLocations],
      });
    },
  });
};

export const useUpdateWanderingBoundary = (): UseMutationResult<WanderingBoundary, Error, WanderingBoundary, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [WanderingBoundaryQueryPaths.UpdateWanderingBoundary],
    mutationFn: (wanderingBoundary: WanderingBoundary) => updateWanderingBoundary(authenticatedInstance, wanderingBoundary),
  });
};

export const useRemoveWanderingBoundary = (): UseMutationResult<void, Error, string, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [WanderingBoundaryQueryPaths.RemoveWanderingBoundary],
    mutationFn: (id: string) => removeWanderingBoundary(authenticatedInstance, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaryLocations],
      });
    },
  });
};

export const useLazyReadWanderingBoundaryPassages = (locationId: string): UseQueryResult<Array<WanderingBoundaryPassage>, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaryPassages, locationId],
    queryFn: ({ signal }) => readWanderingBoundaryPassages(authenticatedInstance, locationId, signal),
    enabled: false,
  });
};

export const useCreateWanderingBoundaryPassage = (): UseMutationResult<
  WanderingBoundaryPassage,
  Error,
  WanderingBoundaryPassage,
  unknown
> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [WanderingBoundaryQueryPaths.CreateWanderingBoundaryPassage],
    mutationFn: (wanderingBoundaryPassage: WanderingBoundaryPassage) =>
      createWanderingBoundaryPassage(authenticatedInstance, wanderingBoundaryPassage),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaryLocations],
      });
    },
  });
};

export const useUpdateWanderingBoundaryPassage = (): UseMutationResult<
  WanderingBoundaryPassage,
  Error,
  WanderingBoundaryPassage,
  unknown
> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [WanderingBoundaryQueryPaths.UpdateWanderingBoundaryPassage],
    mutationFn: (wanderingBoundaryPassage: WanderingBoundaryPassage) =>
      updateWanderingBoundaryPassage(authenticatedInstance, wanderingBoundaryPassage),
  });
};

export const useRemoveWanderingBoundaryPassage = (): UseMutationResult<void, Error, string, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [WanderingBoundaryQueryPaths.RemoveWanderingBoundaryPassage],
    mutationFn: (id: string) => removeWanderingBoundaryPassage(authenticatedInstance, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [WanderingBoundaryQueryPaths.ReadWanderingBoundaryLocations],
      });
    },
  });
};

export const useLazyReadDevices = (locationId: string): UseQueryResult<Array<ReadDevicesResponse>, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [WanderingBoundaryQueryPaths.ReadDevices, locationId],
    queryFn: ({ signal }) => readDevices(authenticatedInstance, locationId, signal),
    enabled: false,
  });
};

export const useLazyReadDomipDevices = (locationId: string): UseQueryResult<Array<ReadDomipDevicesResponse>, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [WanderingBoundaryQueryPaths.ReadDomipDevices, locationId],
    queryFn: ({ signal }) => readDomipDevices(authenticatedInstance, locationId, signal),
    enabled: false,
  });
};
